<template>
  <div class="page-invoices">
    <nav class="breadcrumb mt-5" aria-label="breadcrumbs">
      <ul>
        <li><router-link to="/dashboard">Dashboard</router-link></li>
        <li class="is-active"><router-link to="/dashboard/invoices" aria-current="true">Invoices</router-link></li>
      </ul>
    </nav>

    <div class="columns is-multiline">
      <div class="column is-12">
        <h1 class="title">Invoices</h1>
      </div>

      <div class="column is-12 tabs is-toggle is-small mt-4">
        <ul>
          <li :class="{ 'is-active': localSelectedTab === 'All' }" @click="localSelectedTab = 'All'">
            <a>All</a>
          </li>
          <li :class="{ 'is-active': localSelectedTab === 'Pending Approval' }" @click="localSelectedTab = 'Pending Approval'">
            <a>Pending Approval</a>
          </li>
          <li :class="{ 'is-active': localSelectedTab === 'Submission Failed' }" @click="localSelectedTab = 'Submission Failed'">
            <a>Failed</a>
          </li>
          <li :class="{ 'is-active': localSelectedTab === 'Validated' }" @click="localSelectedTab = 'Validated'">
            <a>Validated</a>
          </li>
          <li :class="{ 'is-active': localSelectedTab === 'Approval Rejected' }" @click="localSelectedTab = 'Approval Rejected'">
            <a>Approval Rejected</a>
          </li>
          <li :class="{ 'is-active': localSelectedTab === 'User Cancelled' }" @click="localSelectedTab = 'User Cancelled'">
            <a>User Cancelled</a>
          </li>
          <li :class="{ 'is-active': localSelectedTab === 'OnHold' }" @click="localSelectedTab = 'OnHold'">
            <a>OnHold</a>
          </li>
          <li :class="{ 'is-active': localSelectedTab === 'Consolidated' }" @click="localSelectedTab = 'Consolidated'">
            <a>Consolidated</a>
          </li>
          <li :class="{ 'is-active': localSelectedTab === 'Queued' }" @click="localSelectedTab = 'Queued'">
            <a>Queued</a>
          </li>
        </ul>
      </div>

      <!-- Toggle Filter and Controls Row -->
      <div class="column is-12">
        <div class="columns is-vcentered" style="align-items: center;">
          <div class="column is-narrow">
            <button
              class="button is-link is-rounded is-small"
              @click="toggleFilters">
              {{ showFilters ? 'Hide Filters' : 'Show Filters' }}
            </button>
          </div>
          <!-- Only show these controls when filters are visible -->
          <template v-if="showFilters">
            <div class="column is-narrow">
              <button class="button is-primary is-rounded is-small" @click="applyFilters">
                Apply Filter
              </button>
            </div>
            <div class="column is-narrow">
              <button
                class="button is-info is-rounded is-small"
                @click="downloadCSV"
                :disabled="!isFilterApplied"
                :title="!isFilterApplied ? 'At least 1 filter must be selected' : ''">
                CSV Report
              </button>
            </div>
            <div class="column is-narrow">
              <button class="button is-danger is-rounded is-small" @click="clearFilters">
                Reset Filters
              </button>
            </div>
            <div class="column is-narrow" style="width: 150px;">
              <div class="select is-link is-small" style="width: 100%;">
                <select v-model="itemsPerPage" @change="changePageSize">
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                </select>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Additional Filter Fields -->
      <div v-if="showFilters" class="column is-12 mb-4" style="padding-left: 20px;">
        <!-- Date Filter Row -->
        <div class="columns is-vcentered" style="gap: 15px; margin-top: 10px;">
          <p class="column is-2"><strong>Date Filter:</strong></p>
          <input v-model="filters.date_start" type="date" class="input is-rounded is-small column is-3" placeholder="Start Date" />
          <input v-model="filters.date_end" type="date" class="input is-rounded is-small column is-3" placeholder="End Date">
        </div>

        <!-- Text-based Filter Fields Row -->
        <div class="columns is-vcentered" style="gap: 15px; margin-top: 10px;">
          <p class="column is-2"><strong>Client Filters:</strong></p>
          <input v-model="filters.buyer_name" type="text" class="input is-rounded is-small" placeholder="Client Name">
          <input v-model="filters.buyer_tin_no" type="text" class="input is-rounded is-small" placeholder="Client TIN Number">
          <input v-model="filters.buyer_id_no" type="text" class="input is-rounded is-small" placeholder="Client ID Number">
          <input v-model="filters.buyer_email" type="text" class="input is-rounded is-small" placeholder="Client Email">
        </div>

        <div class="columns is-vcentered" style="gap: 15px; margin-top: 10px;">
          <p class="column is-2"><strong>Invoice Filters:</strong></p>
          <input v-model="filters.invoice_number" type="text" class="input is-rounded is-small" placeholder="Invoice Number">
          <input v-model="filters.agent_code" type="text" class="input is-rounded is-small" placeholder="Agent Code">
          <input v-model="filters.contract_number" type="text" class="input is-rounded is-small" placeholder="Contract Number">
          <input v-model="filters.lhdn_uuid" type="text" class="input is-rounded is-small" placeholder="LHDN UUID">
        </div>

        <!-- Multiselect Filter Fields Row -->
        <div class="columns is-vcentered" style="gap: 15px; margin-top: 10px;">
          <p class="column is-2"></p>
          <div class="column is-3">
            <multiselect 
              v-model="filters.invoice_type" 
              :options="uniqueInvoiceTypes || []" 
              :multiple="true" 
              placeholder="Select Invoice Type" 
              :close-on-select="false"
              label="description"   
              track-by="code">         
            </multiselect>
          </div>
          
          <div class="column is-3">
            <multiselect 
              v-model="filters.sender_store_code" 
              :options="storeCodes || []" 
              :multiple="true" 
              placeholder="Select Department" 
              :close-on-select="false"
              track-by="id"
              label="code">
            </multiselect>
          </div>

          <div class="column is-3">
            <multiselect 
              v-model="filters.source_system_code"
              :options="systemSourceOptions"
              :multiple="true"
              placeholder="Select Source System Code"
              :close-on-select="false"
              label="label"
              track-by="value"
            >
            </multiselect>
          </div>

        </div>
        <div class="columns is-vcentered" style="gap: 15px; margin-top: 10px;">
          <p class="column is-2"><strong>Consolidated Filters:</strong></p>
          <input v-model="filters.consolidate_invoice_number" type="text" class="input is-rounded is-small" placeholder="Consolidate Invoice No">
          <input v-model="filters.conlidate_line_ref" type="text" class="input is-rounded is-small" placeholder="Consolidate Line Ref">
        </div>
      </div>

      <!-- --------- BULK DOWNLOAD CONTROLS --------- -->
      <div class="column is-12">
        <!-- Download Type Selection -->
        <div class="field is-grouped">
          <div class="control">
            <label class="radio">
              <input type="radio" name="downloadType" value="pdf" v-model="selectedDownloadType">
              Bulk Download PDF
            </label>
          </div>
          <div class="control">
            <label class="radio">
              <input type="radio" name="downloadType" value="csv" v-model="selectedDownloadType">
              Bulk Download Original CSV
            </label>
          </div>
          <div class="control" v-if="isSuperAdmin">
            <label class="radio">
              <input type="radio" name="downloadType" value="resubmit" v-model="selectedDownloadType">
              Bulk Re-trigger Submission
            </label>
          </div>
        </div>
        
        <!-- Info about enabling rules -->
        <div class="content is-small has-text-grey">
          <p v-if="selectedDownloadType === 'pdf'">
            <i class="fas fa-info-circle"></i> Only invoices with "Validated" status can be selected for PDF download.
          </p>
          <p v-if="selectedDownloadType === 'csv'">
            <i class="fas fa-info-circle"></i> Only invoices from IL, PMACS, or IG source systems can be selected for CSV download.
          </p>
          <p v-if="selectedDownloadType === 'resubmit'">
            <i class="fas fa-info-circle"></i> Only invoices with "Submission Failed", "Queued" status, or blank status can be selected for re-trigger submission.
          </p>
        </div>

        <!-- Bulk Download Button -->
        <div v-if="hasChecked" class="mt-3">
          <button
            v-if="selectedDownloadType === 'pdf'"
            class="button is-success is-rounded is-small"
            @click="bulkDownloadPDF()"
          >
            Bulk Generate PDF
          </button>
          <button
            v-else-if="selectedDownloadType === 'csv'"
            class="button is-success is-rounded is-small"
            @click="bulkDownloadOriginalCSV()"
          >
            Bulk Download Original CSV
          </button>
          <button
            v-else-if="selectedDownloadType === 'resubmit'"
            class="button is-warning is-rounded is-small"
            @click="bulkResubmit()"
          >
            Bulk Re-trigger Submission
          </button>
        </div>
      </div>

      <!-- Loading indicator and table omitted for brevity, remains unchanged -->
      <div v-if="loading" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 9999; display: flex; align-items: center; justify-content: center; background: rgba(255, 255, 255, 0.8);">
        <lottie-player
          src="https://assets10.lottiefiles.com/packages/lf20_usmfx6bp.json"
          background="transparent"
          speed="1"
          style="width: 500px; height: 500px;"
          loop
          autoplay>
        </lottie-player>
      </div>

      <div class="column is-12" v-else>
        <table class="table is-fullwidth">
          <thead>
            <tr>
              <th class="is-size-7">
              <input
                type="checkbox"
                :checked="allChecked"
                @change="toggleCheckAll($event)"
              />
            </th>
              <th @click="sortBy('sender.name')" class="is-size-7">
                Client Name <button :class="getSortClass('sender.name')"></button>
              </th>
              <th @click="sortBy('customer.identity_number')" class="is-size-7">
                Client ID <button :class="getSortClass('customer.identity_number')"></button>
              </th>
              <th @click="sortBy('invoice_type')" class="is-size-7">
                Doc Type <button :class="getSortClass('invoice_type')"></button>
              </th>
              <th @click="sortBy('invoice_number')" class="is-size-7">
                Doc Number <button :class="getSortClass('invoice_number')"></button>
              </th>
              <th @click="sortBy('invoice_date')" class="is-size-7">
                Issue Date <button :class="getSortClass('invoice_date')"></button>
              </th>
              <th @click="sortBy('submission_date')" class="is-size-7">
                Submission Date <button :class="getSortClass('submission_date')"></button>
              </th>
              <th @click="sortBy('total_payable_amount')" class="is-size-7">
                Document Total <button :class="getSortClass('total_payable_amount')"></button>
              </th>
              <th @click="sortBy('status')" class="is-size-7">
                Status <button :class="getSortClass('status')"></button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="invoice in invoices" :key="invoice.uuid" @click="openQuickview(invoice)" class="invoice-row">
              <td style="text-align: center">
                <input
                  type="checkbox"
                  :value="invoice.uuid"
                  v-model="checkedInvoices"
                  :disabled="!isInvoiceCheckboxEnabled(invoice)"
                  @click.stop
                />
              </td>
              <td class="is-size-7">{{ invoice.client_name }}</td>
              <td class="is-size-7">{{ invoice.client_id }}</td>
              <td style="text-align: center" class="is-size-7">{{ invoice.invoice_type_description }}</td>
              <td class="is-size-7">{{ invoice.invoice_number }}</td>
              <td class="is-size-7">{{ invoice.invoice_date }}</td>
              <td style="text-align: center" class="is-size-7">
                {{ invoice.submission_date ? formatDate(invoice.submission_date) : '' }}
              </td>
              <td style="text-align: right" class="is-size-7">{{ invoice.invoice_currency + " " + formatCurrency(invoice.total_payable_amount) }}</td>
              <td style="text-align: left" class="is-size-7">
                <a @click.stop="showDetails(invoice)">{{ invoice.status }}</a>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="pagination">
          <button @click="prevPage" :disabled="currentPage === 1" class="button is-ghost is-small">Previous</button>
          <span class="strong is-size-7"> {{ currentPage * itemsPerPage }} of {{ totalDocuments }} Documents</span>
          <button @click="nextPage" :disabled="currentPage === totalPages" class="button is-ghost is-small">Next</button>
        </div>
      </div>
    </div>

    <QuickviewComponent v-if="selectedInvoice" :invoice="selectedInvoice" @close="closeQuickview" @edit="editInvoice" @cancel="cancelInvoice" ref="quickviewComponent" />

    <div class="modal" :class="{ 'is-active': isModalActive }">
      <div class="modal-background"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Submission Details</p>
          <button class="delete" aria-label="close" @click="closeModal"></button>
        </header>
        <section class="modal-card-body">
          <p>{{ modalContent }}</p>
        </section>
        <footer class="modal-card-foot">
          <button class="button" @click="closeModal">Close</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import QuickviewComponent from '@/components/QuickView.vue';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import { mapState } from 'vuex';
import { toast } from 'bulma-toast';


export default {
  name: 'ViewInvoices',
  components: {
    QuickviewComponent,
    Multiselect
  },
  data() {
    return {
      invoices: [],
      selectedDownloadType: 'pdf', // Default to PDF download
      filters: {
        date_start: '',
        date_end: '',
        buyer_name: '',
        invoice_number: '',
        client_name: '',
        client_id: '',
        // Multi-select filters (as arrays)
        invoice_type: [],
        status: [],
        sender_store_code: [], 
        source_system_code: [],
        // Remaining filters
        document_type: '',
        buyer_tin_no: '',
        buyer_id_no: '',
        buyer_email: '',
        // New fuzzy search filters
        agent_code: '',
        contract_number: '',
        lhdn_uuid: '',
        consolidate_invoice_number: '',
        conlidate_line_ref: ''
      },
      systemSourceOptions: [
        { label: 'IL', value: 'IL' },
        { label: 'IG', value: 'IG' },
        { label: 'PMACS', value: 'PMACS' },
        { label: 'Others', value: 'Others' }
      ],
      uniqueInvoiceTypes: [],
      uniqueDocumentTypes: [],
      source_system_code: [],
      localSelectedTab: this.$route.query.tab || 'All',
      itemsPerPage: 50,
      currentPage: 1,
      totalPages: 1,
      totalDocuments: '',
      isModalActive: false,
      modalContent: '',
      sortKey: '',
      sortOrders: {
        'sender_store_code': 1,
        'sender.name': 1,
        'customer.name': 1,
        'customer.identity_number': 1,
        invoice_type: 1,
        invoice_number: 1,
        invoice_date: 1,
        contract_number: 1,
        agent_number: 1,
        submission_date: 1,
        total_payable_amount: 1,
        status: 1,
        source: 1
      },
      selectedInvoice: null,
      loading: true,
      showFilters: false,
      checkedInvoices: []
    };
  },
  watch: {
    localSelectedTab() {
      this.applyFilters();
    },
    selectedDownloadType() {
      // Clear checked invoices when switching download type
      this.checkedInvoices = [];
    }
  },
  mounted() {
    if (this.$route.query.date_start) {
      this.filters.date_start = this.$route.query.date_start;
    }
    if (this.$route.query.date_end) {
      this.filters.date_end = this.$route.query.date_end;
    }
    this.getInvoices();
    this.getUniqueTypes();
  },
  computed: {
    ...mapState({
      storeCodes: state => state.user?.store_code || []
    }),
    isSuperAdmin() {
      return this.$store.getters.isSuperAdmin;
    },
    isFilterApplied() {
      return (
        this.filters.date_start ||
        this.filters.date_end ||
        this.filters.buyer_name ||
        this.filters.invoice_number ||
        (this.filters.invoice_type && this.filters.invoice_type.length) ||
        (this.filters.status && this.filters.status.length) ||
        (this.filters.sender_store_code && this.filters.sender_store_code.length) ||
        this.filters.document_type ||
        this.filters.buyer_tin_no ||
        this.filters.agent_code ||
        this.filters.contract_number ||
        this.filters.buyer_id_no ||
        this.filters.buyer_email ||
        this.filters.lhdn_uuid
      );
    },
    // Returns the list of UUIDs of all currently visible invoices that can be checked based on download type
    validatedUUIDsOnPage() {
      return this.invoices
        .filter(inv => this.isInvoiceCheckboxEnabled(inv))
        .map(inv => inv.uuid);
    },
    // true if every enabled invoice on this page is currently in checkedInvoices
    allChecked() {
      const enabled = this.validatedUUIDsOnPage;
      if (!enabled.length) return false;
      return enabled.every(id => this.checkedInvoices.includes(id));
    },
    // true if at least one enabled invoice is checked
    hasChecked() {
      // only count those in checkedInvoices that are actually enabled on page
      return this.checkedInvoices.some(uuid =>
        this.validatedUUIDsOnPage.includes(uuid)
      );
    }
  },
  methods: {
    // Determine if checkbox should be enabled based on download type and invoice criteria
    isInvoiceCheckboxEnabled(invoice) {
      if (this.selectedDownloadType === 'pdf') {
        // PDF download: only enabled for Validated invoices
        return invoice.status === 'Validated';
      } else if (this.selectedDownloadType === 'csv') {
        // CSV download: only enabled for IL, PMACS, or IG source systems
        return ['IL', 'PMACS', 'IG'].includes(invoice.source_system_code);
      } else if (this.selectedDownloadType === 'resubmit') {
        // Resubmit: only enabled for Submission Failed, Queued, or blank status
        const eligibleStatuses = ['Submission Failed', 'Queued', '', null, undefined];
        return eligibleStatuses.includes(invoice.status);
      }
      return false;
    },
    toggleCheckAll(event) {
      const enabled = this.validatedUUIDsOnPage;
      if (event.target.checked) {
        // check all enabled rows
        this.checkedInvoices = [...new Set([
          // keep any previously‐checked invoices that are enabled elsewhere,
          // but most likely you only care about the current page.
          ...this.checkedInvoices.filter(id => enabled.includes(id)),
          ...enabled
        ])];
      } else {
        // uncheck all enabled rows—leave other selections untouched,
        // though in practice non‐enabled are disabled anyway.
        this.checkedInvoices = this.checkedInvoices.filter(
          id => !enabled.includes(id)
        );
      }
    },
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrders[key] = this.sortOrders[key] * -1;
      } else {
        this.sortKey = key;
        this.sortOrders[key] = 1;
      }
      this.getInvoices();
    },
    getSortClass(key) {
      if (this.sortKey !== key) {
        return '';
      }
      return this.sortOrders[key] === 1 ? 'sort-asc' : 'sort-desc';
    },
    applyFilters() {
      this.currentPage = 1;
      this.getInvoices();
    },
    clearFilters() {
      this.filters = {
        date_start: '',
        date_end: '',
        buyer_name: '',
        invoice_number: '',
        invoice_type: [],
        status: [],
        sender_store_code: [], 
        document_type: '',
        buyer_tin_no: '',
        agent_code: '',
        contract_number: '',
        lhdn_uuid: '',
        buyer_id_no: '',
        buyer_email: '',
        consolidate_invoice_number: '',
        source_system_code: [],
        conlidate_line_ref: ''
      };
      this.localSelectedTab = 'All';  // Reset tab selection if needed
      this.applyFilters();
    },
    getUniqueTypes() {
      axios
        .get('/api/v1/unique-types/')
        .then(response => {
          this.uniqueInvoiceTypes = response.data.invoice_types || [];
          this.uniqueDocumentTypes = response.data.document_types || [];
        })
        .catch(error => {
          console.error('Error fetching unique types:', error);
          this.uniqueInvoiceTypes = [];
          this.uniqueDocumentTypes = [];
        });
    },
    getInvoices() {
      this.loading = true;
      const params = new URLSearchParams();
      if (this.filters.date_start) params.append('dateStart', this.filters.date_start);
      if (this.filters.date_end) params.append('dateEnd', this.filters.date_end);
      if (this.filters.buyer_name) params.append('buyer_name', this.filters.buyer_name);
      if (this.filters.buyer_id_no) params.append('buyer_id_no', this.filters.buyer_id_no);
      if (this.filters.buyer_email) params.append('buyer_email', this.filters.buyer_email);
      if (this.filters.invoice_number) params.append('invoice_number', this.filters.invoice_number);
      if (this.filters.consolidate_invoice_number) params.append('consolidate_invoice_number', this.filters.consolidate_invoice_number);
      if (this.filters.conlidate_line_ref) params.append('conlidate_line_ref', this.filters.conlidate_line_ref);

      if ((this.filters.source_system_code || []).length) {
        const values = this.filters.source_system_code.map(item => item.value);
        params.append('source_system_code', values.join(','));
      }
      // Use a fallback empty array to avoid calling .length on undefined
      if ((this.filters.invoice_type || []).length) {
        const codes = this.filters.invoice_type.map(item => item.code); // 👈 extract only codes
        params.append('invoice_type', codes.join(','));
      }
      if ((this.filters.status || []).length) {
        params.append('status', this.filters.status.join(','));
      }
      if ((this.filters.sender_store_code || []).length) {
        const codes = this.filters.sender_store_code.map(item => item.code);
        params.append('sender_store_code', codes.join(','));
      }
      if (this.filters.document_type) params.append('document_type', this.filters.document_type);
      if (this.filters.buyer_tin_no) params.append('buyer_tin_no', this.filters.buyer_tin_no);
      if (this.filters.agent_code) params.append('agent_code', this.filters.agent_code);
      if (this.filters.contract_number) params.append('contract_number', this.filters.contract_number);
      if (this.filters.lhdn_uuid) params.append('lhdn_uuid', this.filters.lhdn_uuid);

      params.append('page', this.currentPage);
      params.append('page_size', this.itemsPerPage);

      if (this.sortKey) {
        params.append('sort_by', this.sortKey);
        params.append('sort_order', this.sortOrders[this.sortKey] === 1 ? 'asc' : 'desc');
      }

      if (this.localSelectedTab !== 'All') {
        params.append('status', this.localSelectedTab);
      }

      axios
        .get('/api/v1/summary-invoices/', { params })
        .then(response => {
          this.invoices = response.data.results;
          this.totalDocuments = response.data.count;
          this.totalPages = Math.ceil(response.data.count / this.itemsPerPage);
          this.loading = false;
        })
        .catch(error => {
          console.error('Error fetching invoices:', error);
          this.invoices = [];
          this.loading = false;
        });
    },
    showDetails(invoice) {
      this.modalContent = invoice.status_message;
      this.isModalActive = true;
    },
    closeModal() {
      this.isModalActive = false;
    },
    formatCurrency(value) {
      if (!value) return '';
      return parseFloat(value).toLocaleString('en-US', {
        style: 'decimal',
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
      });
    },
    formatCSVValue(value) {
      if (!value) return '';
      if (value.includes(',') || value.includes('\n')) {
        return `"${value}"`;
      }
      return value;
    },
    downloadCSV() {
      if (!this.invoices.length) return;

      // Helper function to flatten nested objects
      const flattenObject = (obj, prefix = '') => {
        let result = {};
        for (const key in obj) {
          if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            if (value && typeof value === 'object' && !Array.isArray(value)) {
              Object.assign(result, flattenObject(value, prefix + key + '_'));
            } else {
              result[prefix + key] = value;
            }
          }
        }
        return result;
      };

      // Flatten all invoices
      const flattenedInvoices = this.invoices.map(invoice => flattenObject(invoice));

      // Use keys from the first flattened invoice as headers
      const headers = Object.keys(flattenedInvoices[0]);
      let csvContent = "data:text/csv;charset=utf-8,";
      csvContent += headers.join(',') + "\n";

      // Build CSV rows based on header order
      flattenedInvoices.forEach(row => {
        const rowValues = headers.map(header => {
          const value = row[header] != null ? row[header].toString() : '';
          return this.formatCSVValue(value);
        });
        csvContent += rowValues.join(',') + "\n";
      });

      const date = new Date();
      const dateString = `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}${date.getDate().toString().padStart(2, '0')}_${date.getHours().toString().padStart(2, '0')}${date.getMinutes().toString().padStart(2, '0')}${date.getSeconds().toString().padStart(2, '0')}`;

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `invoices_${dateString}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.getInvoices();
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.getInvoices();
      }
    },
    changePageSize() {
      this.currentPage = 1;
      this.getInvoices();
    },
    editInvoice(invoice) {
      this.$router.push({ name: 'EditInvoice', params: { uuid: invoice.uuid } });
    },
    openQuickview(invoice) {
      this.selectedInvoice = invoice;
      this.$nextTick(() => {
        this.$refs.quickviewComponent.$el.classList.add('is-active');
      });
    },
    closeQuickview() {
      this.selectedInvoice = null;
      if (this.$refs.quickviewComponent && this.$refs.quickviewComponent.$el) {
        this.$refs.quickviewComponent.$el.classList.remove('is-active');
      }
    },
    cancelInvoice(invoice) {
      axios.post(`/api/v1/invoices/${invoice.uuid}/cancel/`)
        .then(() => {
          this.getInvoices();
          this.closeQuickview();
        })
        .catch(error => {
          console.error('Error cancelling invoice:', error);
        });
    },
    async bulkDownloadPDF() {
      if (!this.checkedInvoices.length) {
        return;
      }

      try {
        const response = await axios.post(
          '/api/v1/pdf/generate-bulk-pdfs/',
          { uuids: this.checkedInvoices }
        );
       if (response.status === 202) {
         window.alert(
           'PDF generation is in progress. You will receive an email with the download link as soon as it’s ready.'
         );
         this.checkedInvoices = [];
       } else {
         window.alert('Unexpected response from server. Please try again later.');
       }
      } catch (error) {
        console.error('Error triggering bulk PDF download:', error);
       window.alert('Failed to request bulk PDF generation. Please try again.');
      }
    },
    async bulkDownloadOriginalCSV() {
      if (!this.checkedInvoices.length) {
        return;
      }

      try {
        const response = await axios.post(
          '/api/v1/csv/extract-original-rows/',
          { 
            uuids: this.checkedInvoices,
            user_email: this.$store.state.user.email
          }
        );
        
        if (response.status === 200 && response.data.status === 'accepted') {
          const data = response.data;
          window.alert(
            `Original CSV extraction started for ${data.uuids_requested} invoices. ` +
            `The extracted rows will be sent to ${this.$store.state.user.email}. ` +
            `Task ID: ${data.task_id}`
          );
          this.checkedInvoices = [];
        } else {
          window.alert('Unexpected response from server. Please try again later.');
        }
      } catch (error) {
        console.error('Error triggering bulk original CSV download:', error);
        
        if (error.response && error.response.status === 400) {
          // Handle validation errors
          const errorData = error.response.data;
          if (errorData.uuids) {
            const uuidErrors = Object.values(errorData.uuids).flat();
            window.alert(`Invalid UUID format detected: ${uuidErrors.join(', ')}`);
          } else {
            window.alert('Bad request. Please check your selection and try again.');
          }
        } else {
          window.alert('Failed to request bulk original CSV extraction. Please try again.');
        }
      }
    },
    async bulkResubmit() {
      if (!this.checkedInvoices.length) {
        return;
      }

      // Show confirmation dialog
      const confirmMessage = `This will re-trigger submission for ${this.checkedInvoices.length} selected invoice(s). Continue?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }

      try {
        const response = await axios.post(
          '/api/v1/integration/retrigger-submission/',
          { uuids: this.checkedInvoices }
        );
        
        if (response.status === 200) {
          toast({
            message: response.data.detail || 
              `Successfully enqueued ${this.checkedInvoices.length} invoice(s) for re-submission.`,
            type: 'is-success'
          });
          this.checkedInvoices = [];
          // Refresh the invoice list to see updated statuses
          this.getInvoices();
        } else {
          toast({
            message: 'Unexpected response from server. Please try again later.',
            type: 'is-warning'
          });
        }
      } catch (error) {
        console.error('Error triggering bulk resubmission:', error);
        
        if (error.response && error.response.status === 400) {
          const errorData = error.response.data;
          toast({
            message: errorData.detail || 'Bad request. Please check your selection and try again.',
            type: 'is-danger'
          });
        } else if (error.response && error.response.status === 403) {
          toast({
            message: 'Access denied. You do not have permission to perform this action.',
            type: 'is-danger'
          });
        } else {
          toast({
            message: 'Failed to trigger bulk resubmission. Please try again.',
            type: 'is-danger'
          });
        }
      }
    },
  }
}
</script>

<style>
.center-align {
  text-align: center;
}

.right-align {
  text-align: right;
}

.sort-asc::after {
  content: ' ▲';
}

.sort-desc::after {
  content: ' ▼';
}

th {
  cursor: pointer;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
}

.pagination button {
  margin: 0 10px;
  padding: 5px 10px;
}

.invoice-row:hover {
  background-color: lightgrey;
  cursor: pointer;
}

.quickview {
  position: fixed;
  top: 0;
  right: -400px;
  width: 400px;
  height: 100%;
  background-color: white;
  box-shadow: -2px 0 5px rgba(0,0,0,0.1);
  transition: right 0.3s ease;
  z-index: 1000;
}
.quickview.is-active {
  right: 0;
}

.loader {
  border: 8px solid #f3f3f3;
  border-radius: 50%;
  border-top: 8px solid #3498db;
  width: 60px;
  height: 60px;
  animation: spin 2s linear infinite;
  margin: 50px auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
